/**
 * sectionPrimitives — the shared "exhibit system" molecules every homepage
 * section composes from (K3 gate, 2026-10-05). Rules, not fills: 1px hairlines,
 * no rounded corners, no tinted chips. Type placement is fixed — DM Serif for
 * H2s + item titles, DM Mono for eyebrows / indexes / meta / captions / CTAs
 * (always caps + tracking), sans for body only. Orange is rationed per section
 * (eyebrow / one decisive figure / one CTA — never icons, checks, rules, chips).
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';

const HAIR = { borderColor: 'var(--color-hairline)' } as const;

export function SectionHeader({
  eyebrow,
  title,
  lede,
}: {
  eyebrow: string;
  title: string;
  lede?: string;
}) {
  return (
    <header>
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text">{eyebrow}</p>
      <h2 className="mt-4 max-w-2xl font-heading text-3xl leading-tight tracking-tight text-text-primary md:text-[2.75rem] md:leading-[1.05]">
        {title}
      </h2>
      {lede && (
        <p className="mt-5 max-w-xl font-body text-base leading-relaxed text-text-secondary lg:text-lg">{lede}</p>
      )}
    </header>
  );
}

/** A top-ruled cell: mono index + serif title + optional meta / body. */
export function ExhibitCell({
  index,
  title,
  meta,
  body,
}: {
  index?: string;
  title: string;
  meta?: string;
  body?: string;
}) {
  return (
    <div className="border-t pt-5" style={HAIR}>
      {index && <span className="font-mono text-[11px] text-text-dim">{index}</span>}
      <h3 className="mt-3 font-heading text-lg leading-snug tracking-tight text-text-primary">{title}</h3>
      {meta && (
        <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.1em] leading-relaxed text-text-dim">{meta}</p>
      )}
      {body && <p className="mt-2.5 font-body text-sm leading-relaxed text-text-secondary">{body}</p>}
    </div>
  );
}

/** Ruled top, mono-caps dim note, optional right-aligned action (a cream link, NOT a CTA). */
export function Provenance({ note, action }: { note: string; action?: ReactNode }) {
  return (
    <div
      className="mt-14 flex flex-col gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-between"
      style={HAIR}
    >
      <p className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] text-text-dim">{note}</p>
      {action}
    </div>
  );
}

/** A cream mono link — one secondary-action species. Underline, no arrow glyph:
 *  the arrow is reserved for the one primary orange CTA (K3 E4 / arrow audit). */
export function SectionLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex shrink-0 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.14em] text-text-secondary underline decoration-[var(--color-border-strong)] underline-offset-4 transition-colors hover:text-text-primary hover:decoration-current"
    >
      {children}
    </Link>
  );
}

/** The one shared primary conversion CTA — squared mono-caps + trailing arrow,
 *  matching the signed-off hero CTA exactly (K3). The arrow lives only here. */
export function PrimaryCTA({ to = '/waitlist', children = 'Request early access' }: { to?: string; children?: ReactNode }) {
  return (
    <Button
      asChild
      size="lg"
      className="!rounded-none !bg-brand px-7 font-mono text-xs font-semibold uppercase tracking-[0.14em] shadow-none hover:!bg-brand-hover"
      style={{ color: 'var(--color-on-brand)' }}
    >
      <Link to={to}>
        {children}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </Button>
  );
}
